(() => {
  if (customElements.get('codex-responsive-video')) return;
  class ResponsiveVideo extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.mobile = matchMedia('(max-width: 749px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.loaded = false;
      this.userPaused = false;
      this.motionOverride = false;
      const options = { signal: this.controller.signal };
      this.toggle = this.querySelector('[data-video-toggle]');
      this.toggle?.addEventListener('click', () => {
        this.loaded = true;
        this.chooseVideo();
        if (!this.video) return;
        if (this.video.paused) {
          this.userPaused = false;
          this.motionOverride = true;
          this.video.play().catch(() => {});
        } else {
          this.userPaused = true;
          this.video.pause();
        }
        this.updateToggle();
      }, options);
      this.near = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.loaded = true;
        this.chooseVideo();
        this.near.disconnect();
      }, { rootMargin: '200px 0px' });
      this.visibility = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.playback();
      }, { threshold: 0.01 });
      this.near.observe(this);
      this.visibility.observe(this);
      this.mobile.addEventListener('change', () => this.chooseVideo(), options);
      this.motion.addEventListener('change', () => {
        this.motionOverride = false;
        this.playback();
      }, options);
      document.addEventListener('visibilitychange', () => this.playback(), options);
      this.updateToggle();
    }
    chooseVideo() {
      if (!this.loaded) return;
      const type = this.mobile.matches && this.querySelector('[data-video-mobile]') ? 'mobile' : 'desktop';
      if (this.type === type) return;
      const template = this.querySelector(`[data-video-${type}]`);
      const source = template?.content.querySelector('video');
      if (!source) return;
      this.releaseVideo();
      this.type = type;
      const video = this.video = source.cloneNode(true);
      video.playsInline = true;
      if (this.hasAttribute('data-autoplay')) video.muted = true;
      video.setAttribute('aria-label', this.dataset.videoTitle || 'Video');
      video.preload = 'metadata';
      const poster = this.querySelector('.cdx-responsive-video__poster');
      if (poster) poster.hidden = false;
      const options = { signal: this.controller.signal };
      video.addEventListener('loadeddata', () => {
        if (video !== this.video) return;
        if (poster) poster.hidden = true;
        this.playback();
      }, options);
      video.addEventListener('error', () => { if (poster) poster.hidden = false; }, options);
      video.addEventListener('pause', () => {
        if (!this.policyPaused && this.visible && !document.hidden) this.userPaused = true;
        this.updateToggle();
      }, options);
      video.addEventListener('play', () => {
        this.policyPaused = false;
        this.userPaused = false;
        if (this.motion.matches) this.motionOverride = true;
        this.updateToggle();
      }, options);
      this.append(video);
      this.playback();
    }
    playback() {
      if (!this.video) return;
      if (!this.visible || document.hidden || this.hasAttribute('data-autoplay') && this.motion.matches && !this.motionOverride) {
        this.policyPaused = true;
        this.video.pause();
      } else if (this.hasAttribute('data-autoplay') && !this.userPaused) {
        this.video.play().catch(() => {});
      }
      this.updateToggle();
    }
    updateToggle() {
      if (!this.toggle) return;
      const paused = !this.video || this.video.paused;
      this.toggle.textContent = paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      this.toggle.setAttribute('aria-label', this.toggle.textContent);
    }
    releaseVideo() {
      if (!this.video) return;
      this.policyPaused = true;
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
      this.video.load();
      this.video.remove();
      this.video = null;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.near?.disconnect();
      this.visibility?.disconnect();
      this.releaseVideo();
      this.type = null;
    }
  }
  customElements.define('codex-responsive-video', ResponsiveVideo);
})();
